/**
 * 项目存储（DataProxy）
 *
 * demo 阶段无后端，项目数据存 DataProxy("demo-projects")。
 * 字段：
 *   - id: 项目 ID（时间戳 + 随机数）
 *   - name: 项目名（默认 "MyFirstBots"）
 *   - code: 玩家 bot 代码（Python）
 *   - createdAt / updatedAt
 *   - commitCount: Git 提交数（屏 4 auto-commit 用）
 *   - levelId: V2 关联的 Level ID（从关卡创建项目时写入，空白项目无此字段）
 *   - mapRef: 关联的地图引用（levelId 或 mapId）
 */

// @ts-ignore — Vite ?raw 静态资源导入,无 .d.ts
import level1Raw from "../../../data/examples/level1_basic.py?raw";
// @ts-ignore
import level2Raw from "../../../data/examples/level2_with_cover.py?raw";
// @ts-ignore
import level3Raw from "../../../data/examples/level3_tactical.py?raw";
// @ts-ignore
import level4Raw from "../../../data/examples/level4_lateral.py?raw";

// V3-005: 读取关卡 rules.json 的 max_units_per_team，用于分叉初始化
import { loadRules } from "../engine/rules-loader";
import { dataProxy } from "../core/data-proxy.js";

export type ProjectType = "user" | "example" | "arena";

export interface Project {
  id: string;
  name: string;
  code: string;
  /** V3: 多坦克代码（tank_codes[i] = 第 i 个坦克的代码）。
   *  1v1 模式：tank_codes = [code]
   *  多坦克模式：tank_codes = [code_0, code_1, code_2]
   *  老数据无此字段 → getTankCodes() fallback 到 [code] */
  tank_codes?: string[];
  /** V3-005(2026-07-13):战略文档（markdown 格式的战术设计）。
   *  仅 2v2/3v3 项目有值（从关卡创建时自动初始化模板），1v1 项目为 undefined。
   *  这是"唯一真相源"——tank_codes 是从此文档经 LLM 翻译生成的制品。
   *  老数据无此字段 → strategy_doc === undefined → 走 1v1 车库模式（向后兼容） */
  strategy_doc?: string;
  createdAt: number;
  updatedAt: number;
  commitCount: number;
  /** FR-024(2026-07-05):项目来源类型。"user"=玩家自己创建,"example"=从内置 examples 复制。
   *  可选字段,老数据(2026-07-05 前创建)无此字段默认按 user 处理,向后兼容 */
  type?: ProjectType;
  /** 仅 example 类型的项目有:指向源 example 的 id(用于"重置"或"升级 example")。
   *  user 项目无此字段(用户创建的项目不绑定源 example) */
  exampleId?: string;
  /** 屏 3 选中的地图 id(2026-07-06 FR-031 / demo P1-#4 升级)。
   *  v1.0.9(2026-07-09):屏 3 已移除,mapId 不再有新写入方(V2 由 levelId 驱动)。
   *  - 历史写入时机:屏 3 map-select change 事件 → saveProject
   *  - 读取时机:屏 4 mount 时(从 project 对象直接读,走 getEffectiveMapId helper)
   *  - 老数据(2026-07-06 前创建)无此字段 → getEffectiveMapId fallback 'battle_easy' */
  mapId?: string;
  /** V2(2026-07-10):关联的 Level ID。从关卡创建项目时写入,空白项目无此字段。
   *  用于屏 1"我的关卡"tab 按 levelId 筛选 + 跳转屏 7 地图编辑器 */
  levelId?: string;
  /** V2(2026-07-10):关联的地图引用,与 levelId 等同(冗余字段方便老代码迁移)。
   *  老数据无此字段 → fallback 到 levelId → mapId → 'battle_easy' */
  mapRef?: string;
}

/** FR-031(2026-07-06)+V2(2026-07-10):获取项目生效的地图 id。
 *  - 优先级:mapId > levelId > mapRef > "battle_easy"
 *  - V2 项目从关卡创建时只设了 levelId/mapRef(无 mapId),如果不走 levelId 兜底
 *    会 fallback 到 battle_easy → 战斗加载旧主图(24 cover/12 obstacle),
 *    跟编辑的关卡(0 cover/0 obstacle)完全对不上
 *  - 所有屏 4 读 project.mapId 的地方统一走这个 helper(屏 3 已于 v1.0.9 移除)
 *  - 返回 string(非 undefined),调用方不需要再处理 nullish */
export function getEffectiveMapId(project: Project | null | undefined): string {
  return project?.mapId ?? project?.levelId ?? project?.mapRef ?? "battle_easy";
}

/** V3: 获取项目的坦克代码数组（向后兼容）。
 *  1v1 项目无 tank_codes → 返回 [project.code] */
export function getTankCodes(project: Project): string[] {
  return project.tank_codes ?? [project.code];
}

/** V3: 保存坦克代码到项目（自动同步 code 字段）。
 *  1v1 时 codes.length === 1，同时更新 project.code */
export function saveTankCodes(project: Project, codes: string[]): void {
  project.tank_codes = codes;
  project.code = codes[0] ?? project.code;
  project.updatedAt = Date.now();
}

// const STORAGE_KEY = "battlecode-demo-projects"; // V12: 已迁移至 DataProxy("demo-projects")

export function generateProjectId(): string {
  return `proj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * 列出项目。
 * @param filter.type 过滤类型("user" | "example"),不传则返回所有
 */
export function listProjects(filter?: { type?: ProjectType }): Project[] {
  const all = dataProxy.get<Project[]>("demo-projects") ?? [];
  const list = filter?.type
    ? all.filter((p) => (p.type ?? "user") === filter.type)
    : all;
  return list.sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getProject(id: string): Project | null {
  return listProjects().find((p) => p.id === id) ?? null;
}

export function saveProject(p: Project): void {
  const list = listProjects();
  const idx = list.findIndex((x) => x.id === p.id);
  p.updatedAt = Date.now();
  if (idx >= 0) list[idx] = p;
  else list.push(p);
  dataProxy.set("demo-projects", list);
}

export function deleteProject(id: string): void {
  const list = listProjects().filter((p) => p.id !== id);
  dataProxy.set("demo-projects", list);
  // P2-#4:级联清理该项目的 git commits(否则删项目后 commits 仍占 localStorage)
  // 动态 import 避免 project-store <-> git-service 循环依赖
  // (实际不会循环,因为 project-store 已被 git-service 通过 getProject 引用)
  import("../services/git-service").then(({ deleteProjectCommits }) => {
    deleteProjectCommits(id);
  }).catch((e) => {
    console.warn("deleteProject: 级联清理 commits 失败", e);
  });
}

export function createProject(
  name: string,
  code: string,
  opts?: { type?: ProjectType; exampleId?: string; levelId?: string; mapRef?: string },
): Project {
  const now = Date.now();
  const p: Project = {
    id: generateProjectId(),
    name,
    code,
    createdAt: now,
    updatedAt: now,
    commitCount: 0,
    type: opts?.type ?? "user",
    exampleId: opts?.exampleId,
    levelId: opts?.levelId,
    mapRef: opts?.mapRef ?? opts?.levelId,
  };
  saveProject(p);
  return p;
}

/**
 * V2(2026-07-10):从 Level 创建新 Project。
 * V3-005(2026-07-13):按关卡 max_units_per_team 分叉初始化 —
 *   1v1:保持现有逻辑（无 strategy_doc）
 *   2v2+:初始化 strategy_doc 模板 + 空 tank_codes
 *
 * @param levelId  关联的 Level ID
 * @param levelName Level 名称（用作默认 project name）
 * @param code      初始代码（来自 Level.playerTemplate 或空白模板）
 * @param pType     项目类型（默认 "user"；Arena 存档用 "arena"）
 * @returns 新创建的 Project
 */
export function createFromLevel(
  levelId: string,
  levelName: string,
  code: string,
  pType: ProjectType = "user",
): Project {
  // V3-005: 读取关卡规则，判断是否多坦克模式
  let unitsPerTeam = 1;
  try {
    const rules = loadRules(levelId);
    unitsPerTeam = rules.constraints?.max_units_per_team ?? 1;
  } catch {
    // loadRules 对未知 levelId fallback 到 battle_easy(1v1)，不会抛异常
    // 但防御性保留 try-catch（仅此一次，因为横切引擎层）
  }

  const project = createProject(levelName, code, {
    type: pType,
    levelId,
    mapRef: levelId,
  });

  if (unitsPerTeam > 1) {
    // 2v2+：初始化战略文档模板 + 空 tank_codes（等待「生成战术」填充）
    project.strategy_doc = generateStrategyTemplate(unitsPerTeam);
    // V3-005 fix: 预分配 N 个空位，确保 getTankCodes() 返回正确长度
    // 否则 totalTanks 从 tank_codes.length || 1 推导时永远为 1（tab/角色卡数量不对）
    project.tank_codes = new Array(unitsPerTeam).fill("");
  }
  // 1v1：保持现有逻辑，不设 strategy_doc 和 tank_codes

  // 重新保存以写入 strategy_doc/tank_codes
  saveProject(project);
  return project;
}

/**
 * V2(2026-07-10):列出关联到指定 levelId 的 Project。
 * 用于屏 1"我的关卡"tab — 检查某个 Level 是否已有项目。
 */
export function listProjectsByLevelId(levelId: string): Project[] {
  return listProjects({ type: "user" }).filter((p) => p.levelId === levelId);
}

// ============================================================
// V3-005(2026-07-13):战略文档模板生成 + 表单数据模型 + markdown 互转
// ============================================================

/** 战术定位枚举（与 from_strategy SYSTEM_PROMPT 中的行为权重对应） */
export const TACTICAL_POSITIONS = ["突击手", "火力支援", "侦察兵", "后卫", "自由人"] as const;
export type TacticalPosition = (typeof TACTICAL_POSITIONS)[number];

/** 攻击偏好选项 */
export const ATTACK_PREFERENCES = [
  "优先攻击最近敌人",
  "优先攻击血量最低",
  "避免主动攻击",
] as const;
export type AttackPreference = (typeof ATTACK_PREFERENCES)[number];

/** 移动模式选项 */
export const MOVEMENT_MODES = ["主动寻敌", "保持距离", "跟随队友", "定点防守"] as const;
export type MovementMode = (typeof MOVEMENT_MODES)[number];

/** 阵型偏好选项 */
export const FORMATIONS = ["三角阵型", "一字横队", "前后梯队", "自由散开"] as const;
export type Formation = (typeof FORMATIONS)[number];

/** 进攻节奏选项 */
export const TEMPO_OPTIONS = ["激进", "均衡", "保守"] as const;
export type Tempo = (typeof TEMPO_OPTIONS)[number];

/** 协同行为选项 */
export const SYNERGY_BEHAVIORS = ["集火", "掩护", "诱敌", "交叉火力"] as const;
export type SynergyBehavior = (typeof SYNERGY_BEHAVIORS)[number];

/** 弹药管理模式 */
export const AMMO_MODES = ["节省弹药", "正常", "疯狂射击"] as const;
export type AmmoMode = (typeof AMMO_MODES)[number];

/** 战术阶段 */
export interface StrategyPhaseData {
  name: string;
  tickRange: string;
  target: string;
  behavior: string;
}

/** 单坦克角色卡（表单数据模型） */
export interface RoleCardData {
  name: string;
  position: TacticalPosition;
  attack: AttackPreference;
  movement: MovementMode;
  /** 跟随目标（仅移动模式=跟随队友时必填） */
  followTarget?: number;
  /** 弹药管理模式 */
  ammoManagement?: AmmoMode;
  /** 特殊行为触发：条件 → 行为 */
  specialTrigger?: string;
  /** 战术细节自由文本 */
  tacticalDetail?: string;
}

/** 协同规则（表单数据模型） */
export interface SynergyRuleData {
  tankA: number;
  tankB: number;
  behavior: SynergyBehavior;
}

/** 作战计划表单数据（完整的结构化模型） */
export interface StrategyFormData {
  intent: string;
  formation: Formation;
  tempo: Tempo;
  /** 全局触发条件：条件 → 行为 */
  globalTrigger?: string;
  /** 战术阶段（可选） */
  phases?: StrategyPhaseData[];
  roles: RoleCardData[];
  synergies: SynergyRuleData[];
}

/**
 * 从 markdown 格式的 strategy_doc 解析为结构化表单数据。
 * 用于：加载已有项目时回填表单。
 * 向后兼容：如果解析失败，返回基于模板的默认数据。
 */
export function parseStrategyDoc(md: string, N: number): StrategyFormData {
  const defaults = defaultStrategyFormData(N);
  if (!md) return defaults;

  try {
    // 解析编队总纲
    const intentMatch = md.match(/战术意图[：:]\s*(.+)/);
    const formationMatch = md.match(/阵型偏好[：:]\s*(.+)/);
    const tempoMatch = md.match(/进攻节奏[：:]\s*(.+)/);
    const globalTriggerMatch = md.match(/全局触发条件[：:]\s*(.+)/);

    if (intentMatch) defaults.intent = intentMatch[1].trim();
    if (formationMatch) {
      const f = formationMatch[1].trim();
      if ((FORMATIONS as readonly string[]).includes(f)) defaults.formation = f as Formation;
    }
    if (tempoMatch) {
      const t = tempoMatch[1].trim();
      if ((TEMPO_OPTIONS as readonly string[]).includes(t)) defaults.tempo = t as Tempo;
    }
    if (globalTriggerMatch) defaults.globalTrigger = globalTriggerMatch[1].trim();

    // 解析战术阶段
    const phasesSection = md.split(/##\s*战术阶段/)[1]?.split(/##\s*角色分工/)[0];
    if (phasesSection) {
      const phaseBlocks = phasesSection.split(/###\s*阶段\s*\d+/).slice(1);
      defaults.phases = [];
      for (const block of phaseBlocks) {
        const nameMatch = block.match(/[:：]\s*(.+)/);
        const tickMatch = block.match(/tick\s*(\d+)\s*[-–—]\s*(\d+)/) || block.match(/(\d+)\s*[-–—]\s*(\d+)/);
        const targetMatch = block.match(/目标[：:]\s*(.+)/);
        const behaviorMatch = block.match(/行为[：:]\s*(.+)/);
        defaults.phases.push({
          name: nameMatch?.[1]?.trim() || "",
          tickRange: tickMatch ? `${tickMatch[1]}-${tickMatch[2]}` : "",
          target: targetMatch?.[1]?.trim() || "",
          behavior: behaviorMatch?.[1]?.trim() || "",
        });
      }
    }

    // 解析角色卡
    const roleBlocks = md.split(/###\s*坦克\s*\d+/).slice(1);
    for (let i = 0; i < Math.min(roleBlocks.length, N); i++) {
      const block = roleBlocks[i];
      const posMatch = block.match(/战术定位[：:]\s*(.+)/);
      const atkMatch = block.match(/攻击偏好[：:]\s*(.+)/);
      const movMatch = block.match(/移动模式[：:]\s*(.+)/);
      const followMatch = block.match(/跟随目标[：:]\s*(\d+)/);
      const ammoMatch = block.match(/弹药管理[：:]\s*(.+)/);
      const triggerMatch = block.match(/特殊行为触发[：:]\s*(.+)/);
      const detailMatch = block.match(/\*\*战术细节[：:]?\*\*\s*\n([\s\S]*?)(?=\n##|\n###|\n\*\*|$)/);

      if (posMatch) {
        const p = posMatch[1].trim();
        if ((TACTICAL_POSITIONS as readonly string[]).includes(p)) defaults.roles[i].position = p as TacticalPosition;
      }
      if (atkMatch) {
        const a = atkMatch[1].trim();
        if ((ATTACK_PREFERENCES as readonly string[]).includes(a)) defaults.roles[i].attack = a as AttackPreference;
      }
      if (movMatch) {
        const m = movMatch[1].trim();
        if ((MOVEMENT_MODES as readonly string[]).includes(m)) defaults.roles[i].movement = m as MovementMode;
      }
      if (followMatch) defaults.roles[i].followTarget = parseInt(followMatch[1], 10);
      if (ammoMatch) {
        const a = ammoMatch[1].trim();
        if ((AMMO_MODES as readonly string[]).includes(a)) defaults.roles[i].ammoManagement = a as AmmoMode;
      }
      if (triggerMatch) defaults.roles[i].specialTrigger = triggerMatch[1].trim();
      if (detailMatch) defaults.roles[i].tacticalDetail = detailMatch[1].trim();
    }

    // 解析协同规则
    const synergySection = md.split(/##\s*协同规则/)[1]?.split(/##/)[0];
    if (synergySection) {
      const synergyLines = synergySection.match(/- 坦克\s*(\d+)\s*[↔<>-]+\s*坦克\s*(\d+)[：:]\s*(.+)/g);
      if (synergyLines) {
        defaults.synergies = [];
        for (const line of synergyLines) {
          const m = line.match(/坦克\s*(\d+)\s*[↔<>-]+\s*坦克\s*(\d+)[：:]\s*(.+)/);
          if (m) {
            const b = m[3].trim();
            if ((SYNERGY_BEHAVIORS as readonly string[]).includes(b)) {
              defaults.synergies.push({
                tankA: parseInt(m[1], 10),
                tankB: parseInt(m[2], 10),
                behavior: b as SynergyBehavior,
              });
            }
          }
        }
      }
    }

    return defaults;
  } catch {
    return defaults;
  }
}

/**
 * 将表单数据构建为 markdown 格式的 strategy_doc。
 * 用于：「生成战术」前将表单内容序列化为 LLM 可消费的格式。
 */
export function buildStrategyDoc(form: StrategyFormData): string {
  const lines: string[] = [];
  lines.push("# 编队战术");
  lines.push("");
  lines.push("## 编队总纲");
  lines.push(`- 战术意图：${form.intent || "等待玩家填写"}`);
  lines.push(`- 阵型偏好：${form.formation}`);
  lines.push(`- 进攻节奏：${form.tempo}`);
  if (form.globalTrigger) lines.push(`- 全局触发条件：${form.globalTrigger}`);
  lines.push("");

  // 战术阶段（可选）
  if (form.phases && form.phases.length > 0) {
    lines.push("## 战术阶段");
    lines.push("");
    for (let i = 0; i < form.phases.length; i++) {
      const ph = form.phases[i];
      lines.push(`### 阶段 ${i + 1}：${ph.name}（tick ${ph.tickRange}）`);
      if (ph.target) lines.push(`- 目标：${ph.target}`);
      if (ph.behavior) lines.push(`- 行为：${ph.behavior}`);
      lines.push("");
    }
  }

  lines.push("## 角色分工");
  lines.push("");

  for (let i = 0; i < form.roles.length; i++) {
    const r = form.roles[i];
    lines.push(`### 坦克 ${i} - ${r.name}`);
    lines.push(`- 战术定位：${r.position}`);
    lines.push(`- 攻击偏好：${r.attack}`);
    lines.push(`- 移动模式：${r.movement}`);
    if (r.followTarget !== undefined) lines.push(`- 跟随目标：${r.followTarget}`);
    if (r.ammoManagement) lines.push(`- 弹药管理：${r.ammoManagement}`);
    if (r.specialTrigger) lines.push(`- 特殊行为触发：${r.specialTrigger}`);
    if (r.tacticalDetail) {
      lines.push("");
      lines.push("**战术细节：**");
      lines.push(r.tacticalDetail.trim());
    }
    lines.push("");
  }

  lines.push("## 协同规则");
  for (const s of form.synergies) {
    lines.push(`- 坦克 ${s.tankA} ↔ 坦克 ${s.tankB}：${s.behavior}`);
  }

  return lines.join("\n");
}

/**
 * 生成默认的表单数据（新项目首次进入战术部时的初始值）。
 */
export function defaultStrategyFormData(N: number): StrategyFormData {
  return buildFormDataFromRoles(N, defaultRoles(N));
}

function buildFormDataFromRoles(N: number, roles: Array<{ name: string; position: string; attack: string; movement: string }>): StrategyFormData {
  const synergies: SynergyRuleData[] = [];
  if (N >= 2) {
    for (let i = 0; i < N - 1; i++) {
      synergies.push({ tankA: i + 1, tankB: (i + 2) % N || 0, behavior: "集火" });
    }
  }

  return {
    intent: "等待玩家填写",
    formation: N === 2 ? "一字横队" : "三角阵型",
    tempo: "均衡",
    roles: roles.map((r) => ({
      name: r.name,
      position: r.position as TacticalPosition,
      attack: r.attack as AttackPreference,
      movement: r.movement as MovementMode,
      ammoManagement: "正常" as AmmoMode,
    })),
    synergies,
  };
}

/**
 * 生成默认角色分工数组（N 辆坦克）。
 * 默认分工：坦克 0 = 突击手（前锋），其余 = 火力支援（两翼）。
 */
function defaultRoles(N: number): Array<{
  name: string;
  position: string;
  attack: string;
  movement: string;
}> {
  const roles: Array<{ name: string; position: string; attack: string; movement: string }> = [];
  for (let i = 0; i < N; i++) {
    if (i === 0) {
      roles.push({ name: "前锋·铁壁", position: "突击手", attack: ATTACK_PREFERENCES[0], movement: MOVEMENT_MODES[0] });
    } else if (i === 1) {
      roles.push({ name: "左翼·利刃", position: "火力支援", attack: ATTACK_PREFERENCES[1], movement: MOVEMENT_MODES[1] });
    } else {
      roles.push({ name: "右翼·尖刀", position: "火力支援", attack: ATTACK_PREFERENCES[1], movement: MOVEMENT_MODES[1] });
    }
  }
  return roles;
}

/**
 * V3-005:生成初始战略文档模板（markdown 格式）。
 * 首次进入战术部时预填此模板，降低玩家从零开始的心智负担。
 *
 * @param N 每队坦克数（2 或 3）
 * @returns markdown 格式的战略文档模板
 */
export function generateStrategyTemplate(N: number, templateId?: "triangle" | "defense" | "pincer"): string {
  const formData = buildTemplateFormData(N, templateId);
  return buildStrategyDoc(formData);
}

/** 模板定义 */
export interface StrategyTemplateDef {
  id: string;
  name: string;
  description: string;
  suitableFor: "2v2" | "3v3" | "both";
}

export const STRATEGY_TEMPLATES: StrategyTemplateDef[] = [
  {
    id: "triangle",
    name: "三角突击",
    description: "前锋正面吸引火力，两翼包抄集火。经典3v3编队。",
    suitableFor: "3v3",
  },
  {
    id: "defense",
    name: "防守反击",
    description: "后卫驻守防线，双侦察兵诱敌回撤，掩体伏击。适合稳扎稳打。",
    suitableFor: "3v3",
  },
  {
    id: "pincer",
    name: "双翼包抄",
    description: "双自由人左右迂回形成交叉火力。适合主动出击的2v2编队。",
    suitableFor: "2v2",
  },
];

function buildTemplateFormData(N: number, templateId?: string): StrategyFormData {
  if (N === 3 && templateId === "defense") {
    return buildFormDataFromRoles(N, [
      { name: "后卫·壁垒", position: "后卫", attack: "优先攻击最近敌人", movement: "定点防守" },
      { name: "左路·斥候", position: "侦察兵", attack: "避免主动攻击", movement: "保持距离" },
      { name: "右路·鹰眼", position: "侦察兵", attack: "避免主动攻击", movement: "保持距离" },
    ]);
  }
  if (N === 2 && templateId === "pincer") {
    return buildFormDataFromRoles(N, [
      { name: "左翼·暗刃", position: "自由人", attack: "优先攻击血量最低", movement: "保持距离" },
      { name: "右翼·影刺", position: "自由人", attack: "优先攻击血量最低", movement: "保持距离" },
    ]);
  }
  // 默认：三角突击
  return defaultStrategyFormData(N);
}

/**
 * FR-025(2026-07-08):fork 后重定向所有关联 Project 到新 levelId。
 *
 * 背景：屏 7 编辑内置关卡时，保存自动 fork 到 _user 目录生成新 levelId，
 * 但之前 Project 的 levelId/mapRef 仍指向旧 ID，导致屏 4 加载原始地图。
 *
 * 此函数：
 * - 找到所有 levelId 或 mapRef 等于 oldLevelId 的 Project
 * - 统一更新为新 levelId
 * - 写回 localStorage
 *
 * 调用方：屏 7 doSave 的 onForked 回调中调用。
 */
export function redirectProjectLevel(oldLevelId: string, newLevelId: string): void {
  const all = dataProxy.get<Project[]>("demo-projects");
  if (!all) return;

  let updated = false;
  for (const p of all) {
    if (p.levelId === oldLevelId || p.mapRef === oldLevelId) {
      p.levelId = newLevelId;
      p.mapRef = newLevelId;
      p.updatedAt = Date.now();
      updated = true;
    }
  }

  if (updated) {
    dataProxy.set("demo-projects", all);
  }
}

// ============================================================
// FR-024 同步(2026-07-05):L1/L2 example code 直接 import data/examples/*.py ?raw
//   这样 example SO 永远 = data/examples/下的 .py,project-store 不再持过期副本
//   Vite ?raw 把 .py 内容作为字符串内联(无需运行时 fetch)
//   注:Vite 在 .ts/.tsx/.html/.css 之外的 .py 需先在 vite.config.ts 配置 assetsInclude
// ============================================================
/** Level 1 example 注入到屏 1 卡片的代码。SO = data/examples/level1_basic.py(v1.0.4,两阶段 scan+lkp)。
 * 之前 DEFAULT_BOT_CODE 在本文件持 v1.0.2 旧版(1327 chars,无 scan),跟 GDD 设计的 L1 教学承诺断;
 * 现在改用 ?raw import,example SO 单一真相,project-store 不再漂移。 */
export const DEFAULT_BOT_CODE: string = level1Raw;

/** Level 2 example 注入到屏 1 卡片的代码。SO = data/examples/level2_with_cover.py(v1.0.4,HP<50% 找掩体 + 两阶段 scan+lkp)。
 * 之前 DEFAULT_BOT_CODE_L2 在本文件持 v1.0.3 简化版(837 chars,scan 教学被砍);
 * 现在改用 ?raw import。 */
export const DEFAULT_BOT_CODE_L2: string = level2Raw;

// ============================================================
// FR-024(2026-07-05):本地 Example 库 — 屏 1 Examples tab 数据源
// ============================================================
/**
 * Example 定义。屏 1 Examples tab 列这些卡片,点 Load 复制到 user projects。
 * 新增 example 只在这里 push 一条,无需改 UI。
 */
export interface ExampleDef {
  /** 固定 id(用于 loadExampleAsProject) */
  id: string;
  /** 卡片标题(如 "L1 — Basic") */
  name: string;
  /** 教学难度等级,仅用于排序和 badge 显示 */
  level: 1 | 2 | 3 | 4;
  /** 一句话说明教学目标(屏 1 卡片副标题) */
  description: string;
  /** 完整 bot code(注入到新项目) */
  code: string;
}

/** Level 3 example 注入到屏 1 卡片的代码。SO = data/examples/level3_tactical.py(v1.0.4)。 */
const EXAMPLE_L3_CODE: string = level3Raw;

/** Level 4 example 注入到屏 1 卡片的代码。SO = data/examples/level4_lateral.py(v1.0.5,侧翼绕后 — 8 向移动教学)。 */
const EXAMPLE_L4_CODE: string = level4Raw;

export const EXAMPLES: ExampleDef[] = [
  {
    id: "ex-l1-basic",
    name: "L1 — Basic",
    level: 1,
    description: "推到中点,看见敌人就开火。最简单的打法。",
    code: DEFAULT_BOT_CODE,
  },
  {
    id: "ex-l2-cover",
    name: "L2 — With Cover",
    level: 2,
    description: "教 nearest_cover（属性） — 看见敌人后躲掩体,血量 > 30 才开火。",
    code: DEFAULT_BOT_CODE_L2,
  },
  {
    id: "ex-l3-tactical",
    name: "L3 — Tactical",
    level: 3,
    description: "HP < 50% 躲掩体,看不见时两阶段 scan+追 lkp,KILL FEED 留 trace。",
    code: EXAMPLE_L3_CODE,
  },
  {
    id: "ex-l4-lateral",
    name: "L4 — Lateral Flanker",
    level: 4,
    description: "教 8 向移动 — 先 y 推到侧翼 y=4(8 tick 纯垂直),再 x 横切(切比雪夫斜向)。补 v1.0.4 切比雪夫归一 0 实战数据。",
    code: EXAMPLE_L4_CODE,
  },
];

export function listExamples(): ExampleDef[] {
  return EXAMPLES.slice().sort((a, b) => a.level - b.level);
}

export function getExample(id: string): ExampleDef | null {
  return EXAMPLES.find((e) => e.id === id) ?? null;
}

/**
 * 从 example 创建一个新的 user project(复制 code + 标记 exampleId)。
 * @returns 新创建的 Project(type="user",exampleId 指向源 example)
 */
export function loadExampleAsProject(exampleId: string, customName?: string): Project | null {
  const ex = getExample(exampleId);
  if (!ex) return null;
  const name = customName?.trim() || `${ex.name} (${Date.now().toString(36)})`;
  return createProject(name, ex.code, { type: "user", exampleId });
}
